import { ColumnDef } from '@tanstack/react-table';
import { cn } from '@/utils/styles';

import { useCountries } from '@/hooks';

import { Chip, Icon, Typography } from '@/components';
import { customRound, europeanFormatNumber, orderTechTransactionIcons } from '@/helpers';
import { getChipFromOpportunity } from '@/features/opportunities/helpers';
import { IconName } from '@/components/icons/types';
import { MyOpportunitiesLocation, Opportunity, Technology, Step, StepKey } from './types';

const STATUS_STEPS: Step[] = [
  { key: 'draft', label: 'Deal Preparation', color: 'warning' },
  { key: 'pending', label: 'Under Review', color: 'warning' },
  { key: 'published', label: 'Published', color: 'success' },
  { key: 'closed', label: 'Closed', color: 'disabled' },
  { key: 'negotiations', label: 'Negotiations', color: 'display' },
  { key: 'deal_closed', label: 'Deal Closed', color: 'error' },
  { key: 'deal_cancelled', label: 'Deal Closed', color: 'error' },
];

const normalizedStep = (status: any, closedType: any) => {
  const STEP_BY_KEY: Record<StepKey, Step> = Object.fromEntries(STATUS_STEPS.map((s) => [s.key, s])) as Record<
    StepKey,
    Step
  >;

  const rawKey = status === 'closed' ? (closedType ?? 'closed') : status;
  const key = typeof rawKey === 'string' && rawKey in STEP_BY_KEY ? (rawKey as StepKey) : undefined;
  return key ? STEP_BY_KEY[key] : undefined;
};

const TechnologyIcons = ({ technologies }: { technologies: Technology }) => {
  const types = technologies.types;
  return (
    <div className="flex">
      {orderTechTransactionIcons(types || []).map((technology, i) => (
        <Icon name={technology as IconName} className="text-primary-700 mr-2 h-6 w-6" key={`${technology}-${i}`} />
      ))}
    </div>
  );
};

const CapacityDisplay = ({ capacity, hasPV }: { capacity: number; hasPV: boolean }) => {
  const roundedCapacity = customRound(capacity);
  const formattedCapacity = europeanFormatNumber(roundedCapacity);
  const capacityToDisplay = capacity === 0 ? '' : hasPV ? `${formattedCapacity} MWp` : `${formattedCapacity} MW`;
  return (
    <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
      <Typography as="p" size="body-2" weight="normal" className="text-left">
        {capacityToDisplay}
      </Typography>
    </div>
  );
};

const LocationDisplay = ({ location }: { location: MyOpportunitiesLocation[] }) => {
  const { options: countries } = useCountries();
  const transactionCountries: string[] = location.flatMap((location) =>
    Array.isArray(location.country) ? location.country : [location.country]
  );
  const countryLabels = transactionCountries.map((tc) => countries.find((c) => c.value === tc)?.label || tc).join(', ');
  const formattedCity = location.map((location) => location.city.join(', '));
  return (
    <>
      <div className="max-w-lg min-w-0">
        <Typography as="p" size="body-2" weight="normal" className="text-left">
          {countryLabels}
        </Typography>
      </div>
      <div className="max-w-lg min-w-0 leading-relaxed break-words whitespace-normal">
        <Typography as="p" size="body-3" weight="normal" className="text-left text-neutral-400">
          ({formattedCity})
        </Typography>
      </div>
    </>
  );
};

const OpportunitiesDisplay = ({ opportunity }: { opportunity: Opportunity }) => {
  const formattedName: string = opportunity.name ?? '-';
  const totalProjects: number = opportunity.projects.length;
  const chip = !!opportunity.isPaid ? getChipFromOpportunity(opportunity) : null;
  return (
    <div className="relative max-w-lg min-w-0 pt-0 leading-relaxed break-words">
      {chip && (
        <div className="absolute -top-5 left-0 z-10">
          <Chip color={chip.color}>
            <Typography as="span" size="micro" weight="semibold" className="text-left text-[8px]">
              {chip.name}
            </Typography>
          </Chip>
        </div>
      )}
      <div className={cn(chip && 'mt-2', 'flex flex-col pt-1')}>
        <Typography as="p" size="body-2" weight="semibold" className="text-left">
          {formattedName}
        </Typography>
        {totalProjects > 1 && (
          <Typography as="p" size="body-3" weight="normal" className="text-left text-neutral-400">
            {totalProjects} Projects
          </Typography>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status, closedType }: { status: string; closedType?: string | null }) => {
  const step = normalizedStep(status, closedType);
  return (
    <Chip className="text-xxs px-2 py-1" color={(step?.color as any) ?? 'default'}>
      {step?.label ?? 'Unknown'}
    </Chip>
  );
};

const EOINumber = ({ offers }: { offers: any }) => {
  const visibleStatuses = ['pending', 'accepted', 'rejected'];
  const totalCount = offers.filter((offer: any) => visibleStatuses.includes(offer.status)).length;
  return <div>{totalCount}</div>;
};

export const myOppsColumns = (): ColumnDef<Opportunity>[] => [
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <StatusBadge status={row.original.status.name} closedType={row.original.closedType} />,
  },
  {
    accessorKey: 'name',
    header: 'Opportunity',
    cell: ({ row }) => <OpportunitiesDisplay opportunity={row.original} />,
  },
  {
    accessorKey: 'technologies',
    header: 'Technology',
    cell: ({ row }) => <TechnologyIcons technologies={row.original.technologies} />,
  },
  {
    accessorKey: 'capacity',
    header: 'Capacity',
    cell: ({ row }) => <CapacityDisplay capacity={row.original.technologies.capacity} hasPV={row.original.hasPV} />,
  },
  {
    accessorKey: 'location',
    header: 'Location',
    cell: ({ row }) => <LocationDisplay location={row.original.location} />,
  },
  {
    accessorKey: 'eois',
    header: 'EOIs',
    cell: ({ row }) => <EOINumber offers={row.original.offers} />,
  },
  {
    accessorKey: 'alerts',
    header: 'Alerts',
    cell: () => {
      //TODO: Modificar cuando vengan datos del objeto "alerts".
      const alerts: any = [];

      return (
        <div className="flex items-start">
          {alerts.length ? (
            <span className="bg-secondary-100 text-secondary-700 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium">
              <Icon name="triangle-alert" className="h-3 w-3 text-red-900" />
              <Typography size="body-3" className="text-red-900">
                2
              </Typography>
            </span>
          ) : (
            <Typography className="text-muted-foreground" size="body-3">
              —
            </Typography>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'details',
    header: 'See details',
    cell: () => (
      <div className="flex w-full justify-center">
        <Icon
          name="eye-open"
          className="hover:bg-primary-700 text-primary-700 h-8 w-8 rounded-sm p-1 transition duration-200 ease-in-out hover:text-white"
        />
      </div>
    ),
  },
];
